import { useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@shop/api-client'
import type { Warehouse } from '@shop/api-client'
import { BRAND } from '@shop/shared'
import { Banner, ErrorBanner, Spinner } from '@shop/ui'
import { Sheet, TouchButton } from '../pos/touch'
import { PosPerm } from '../pos/contracts'
import type { CustomerSummary, SaleDetail } from '../pos/contracts'
import { useStation } from '../pos/station'
import * as pos from '../pos/api'
import OpenShift from './OpenShift'
import Sell from './Sell'
import Payment from './Payment'
import Receipt from './Receipt'
import Returns from './Returns'
import CloseShift from './CloseShift'
import Dashboard from './Dashboard'
import CustomerPicker from './CustomerPicker'
import SyncBar from '../offline/SyncBar'
import { rememberShift, useSnapshot } from '../offline/snapshot'

/**
 * الصندوق — <b>القشرةُ التي تُرتّب الشاشات</b>.
 *
 * وترتيبُ اليوم: <b>ورديّةٌ تُفتح ⇒ بيعٌ ⇒ دفعٌ ⇒ فاتورة</b>، ومعها المرتجعُ
 * واللوحةُ والإغلاق. <b>ولا شاشةَ إدارةٍ هنا</b>: لا منتجاتٍ تُنشأ، ولا
 * مستخدمين، ولا مشتريات — وحدُّ المالك أنّ نقطةَ البيع لا تحمل شاشاتِ الإدارة.
 *
 * ⚠ <b>والصلاحيّاتُ تُقرأ من الجلسة لا تُشتقّ</b>: ما يظهر من أزرارٍ يتبع ما
 *   ردّه <c>/me</c>. <b>والخادمُ يرفض على أيّ حال</b> — والإخفاءُ راحةٌ
 *   للكاشير لا حراسة.
 */
type View = 'sell' | 'pay' | 'receipt' | 'returns' | 'close' | 'board' | 'customer' | 'switch'

export default function Counter() {
  const { session, logout } = useAuth()
  const station = useStation()

  const [sale, setSale] = useState<SaleDetail | null>(null)
  const [completed, setCompleted] = useState<SaleDetail | null>(null)
  const [customer, setCustomer] = useState<CustomerSummary | null>(null)
  const [view, setView] = useState<View>('sell')

  const has = useCallback(
    (code: string) => session?.me?.permissions.includes(code) ?? false,
    [session],
  )

  const branchId = station.branchId
  const queries = useQueryClient()

  // ⚠ **والورديّةُ تُقرأ من الخادم لا من ذاكرة المتصفّح**: الكاشيرُ قد يكون
  //   أغلقها من جهازٍ آخر، وصندوقٌ يبيع على ورديّةٍ أُغلقت يُنتج فاتورةً
  //   يرفضها الخادم — بعد أن يكون الزبونُ دفع.
  const shift = useQuery({
    queryKey: ['shift', branchId],
    queryFn: () => pos.currentShift(branchId!),
    enabled: branchId !== null,
  })

  const refreshShift = useCallback(() => {
    void queries.invalidateQueries({ queryKey: ['shift', branchId] })
  }, [queries, branchId])

  const current = branchId ? (shift.data ?? null) : null

  // ⚠ **ونسخةُ الجهاز تُحدَّث وهو متّصل** (`0052`) — الأصنافُ والأسماءُ والورديّة،
  //   ليُكمل البيعَ إن انقطع النت فجأة.
  const userId = session?.me.userId ?? null

  useSnapshot(branchId && station.warehouseId ? { branchId, warehouseId: station.warehouseId } : null)

  useEffect(() => {
    if (userId && shift.isSuccess) {
      rememberShift(shift.data ?? null, userId)
    }
  }, [shift.isSuccess, shift.data, userId])

  if (station.loading || (branchId !== null && shift.isPending)) {
    return (
      <div className="grid min-h-dvh place-content-center">
        <Spinner label="يُجهَّز الصندوق…" />
      </div>
    )
  }

  if (!branchId) {
    return (
      <Blocked onLogout={() => void logout()}>
        لا فرعَ مُفعَّلٌ لحسابك. راجِع المديرَ ليمنحك فرعًا.
      </Blocked>
    )
  }

  if (station.warehouses.length === 0) {
    return (
      <Blocked onLogout={() => void logout()}>
        لا مستودعَ ممنوحٌ لك في هذا الفرع. راجِع المديرَ ليمنحك مستودعًا.
      </Blocked>
    )
  }

  // ⚠ **ولا بيعَ قبل ورديّة** — قرارُ المالك، ولو كان البيعُ بالبطاقة وحدَها.
  if (!current) {
    if (!has(PosPerm.shiftOpen)) {
      return (
        <Blocked onLogout={() => void logout()}>
          لا ورديّةَ مفتوحة، ولا تملك صلاحيّةَ فتحها.
        </Blocked>
      )
    }

    return (
      <OpenShift
        branchId={branchId}
        warehouses={station.warehouses}
        warehouseId={station.warehouseId}
        onChooseWarehouse={station.choose}
        onOpened={refreshShift}
      />
    )
  }

  // ⚠ **ولا مستودعَ افتراضيٌّ حين يكون أكثرَ من واحد.**
  //
  //   كان هنا `?? station.warehouses[0].id` — وهو يُسقط الحارسَ الذي وُضع في
  //   `useStation` بعينه: أوّلُ ما يردّه الخادمُ قد يكون **«التالف»**، فيبيع
  //   الصندوقُ منه بلا أن يلاحظ أحد. **ولا يُكتشف ذلك إلا بجرد.**
  //
  //   ويقع بعد كلّ تحديثِ صفحةٍ والورديّةُ مفتوحة: الاختيارُ في الذاكرة يضيع،
  //   ولا تمرّ شاشةُ فتح الورديّة لتسأل من جديد. **فيُسأل هنا.**
  if (!station.warehouseId) {
    return (
      <WarehouseGate
        warehouses={station.warehouses}
        onChoose={station.choose}
        onLogout={() => void logout()}
      />
    )
  }

  const warehouseId = station.warehouseId

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-10 flex min-h-14 items-center justify-between gap-2 border-b border-line bg-surface px-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{BRAND.nameAr}</p>
          <p className="tabular truncate text-xs text-ink-soft" dir="ltr">
            {current.sessionNo} · {session?.me?.username}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {has(PosPerm.dashboardMine) && (
            <HeaderButton label="اللوحة" onClick={() => setView('board')} />
          )}

          {has(PosPerm.returnCreate) && (
            <HeaderButton label="مرتجع" onClick={() => setView('returns')} />
          )}

          {has(PosPerm.shiftClose) && (
            <HeaderButton label="إغلاق" onClick={() => setView('close')} />
          )}

          {/*
            ⚠ **وزرُّ الخروج كان مفقودًا هنا وحدَه.** كان يظهر في شاشة
              المستودع وفي شاشات المنع — وكلُّها لا تُرى حين يكون المستودعُ
              واحدًا والورديّةُ مفتوحة. فكان تبديلُ الكاشير يعني مسحَ بيانات
              الموقع من المتصفّح، أو نافذةً خفيّة.
          */}
          <HeaderButton label="خروج" onClick={() => setView('switch')} />
        </div>
      </header>

      <SyncBar />

      <ErrorBanner error={shift.error ?? station.error} />

      {customer && (
        <div className="flex items-center justify-between gap-2 border-b border-line bg-surface-raised px-3 py-2 text-sm">
          <span className="truncate">
            {customer.nameAr}
            <span className="tabular mr-2 text-xs text-ink-soft" dir="ltr">
              {customer.phone}
            </span>
          </span>

          <button className="min-h-11 px-2 text-xs underline" type="button" onClick={() => setCustomer(null)}>
            أزِل
          </button>
        </div>
      )}

      {!customer && has(PosPerm.customerView) && (
        <button
          className="w-full border-b border-line px-3 py-2 text-right text-xs text-ink-soft"
          type="button"
          onClick={() => setView('customer')}
        >
          + أضِف عميلًا (اختياريّ)
        </button>
      )}

      <Sell
        branchId={branchId}
        warehouseId={warehouseId}
        canOverridePrice={has(PosPerm.priceOverride)}
        canDiscount={has(PosPerm.discountOverride)}
        sale={sale}
        onSaleChanged={setSale}
        onPay={() => setView('pay')}
      />

      {view === 'pay' && sale && (
        <Payment
          sale={sale}
          onClose={() => setView('sell')}
          onDone={(done) => {
            setCompleted(done)
            setSale(null)
            setCustomer(null)
            setView('receipt')
          }}
        />
      )}

      {view === 'receipt' && completed && (
        <Receipt
          saleId={completed.header.id}
          onClose={() => {
            setCompleted(null)
            setView('sell')
          }}
        />
      )}

      {view === 'customer' && (
        <CustomerPicker
          onClose={() => setView('sell')}
          onPick={(picked) => {
            setCustomer(picked)
            setView('sell')

            // ⚠ **والعميلُ يُربط عند إنشاء المسوّدة**، فإن كانت مفتوحةً تُلغى
            //   السلّةُ المحلّيّةُ وتُبنى من جديد. وهذا نادرٌ عمدًا: الكاشيرُ
            //   يسأل عن الهاتف قبل المسح.
            if (picked && sale) {
              setSale(null)
            }
          }}
        />
      )}

      {view === 'returns' && (
        <Returns canRefund={has(PosPerm.refundPost)} onClose={() => setView('sell')} />
      )}

      {view === 'board' && (
        <Dashboard
          canRefund={has(PosPerm.refundPost)}
          canPayout={has(PosPerm.cashPayout)}
          canPunch={has(PosPerm.attendancePunch)}
          session={current}
          onClose={() => setView('sell')}
        />
      )}

      {view === 'close' && (
        <CloseShift
          session={current}
          onClose={() => setView('sell')}
          onClosed={() => {
            refreshShift()
            setSale(null)
            setView('sell')
          }}
        />
      )}

      {view === 'switch' && (
        <SwitchUser
          username={session?.me?.username ?? ''}
          sessionNo={current.sessionNo}
          hasCart={(sale?.lines?.length ?? 0) > 0}
          onStay={() => setView('sell')}
          onLeave={() => void logout()}
        />
      )}
    </div>
  )
}

/**
 * تبديلُ الموظّف — <b>ولا يُخرج أحدًا بضغطةٍ واحدة</b>.
 *
 * ⚠ <b>والورديّةُ تبقى مفتوحةً باسم من فتحها</b>: هي لكلِّ موظّفٍ على حدة
 * (<c>CurrentAsync</c> تسأل عن ورديّة <b>هذا</b> الكاشير في هذا الفرع)، فمن
 * يدخل بعده لا يرث درجَه ولا يستطيع إغلاقَه، بل يفتح ورديّتَه. وهذا مقصود:
 * فرقُ الدرج يُنسب إلى من عدّه.
 *
 * <p>
 * ولذلك يُقال هنا صراحةً بدل أن يُكتشف عند الإغلاق.
 * </p>
 */
function SwitchUser({
  username,
  sessionNo,
  hasCart,
  onStay,
  onLeave,
}: {
  username: string
  sessionNo: string
  hasCart: boolean
  onStay: () => void
  onLeave: () => void
}) {
  return (
    <Sheet title="خروجٌ وتبديلُ موظّف" onClose={onStay}>
      <div className="mx-auto grid max-w-md content-start gap-3">
      <p className="text-center text-sm text-ink-soft">
        الحسابُ الحاليّ <span dir="ltr">{username}</span> · الورديّة{' '}
        <span className="tabular" dir="ltr">{sessionNo}</span>
      </p>

      <Banner tone="info">
        ورديّتُك تبقى مفتوحةً باسمك، ولا يُغلقها غيرُك. ومن يدخل بعدك يفتح
        ورديّتَه هو — فيبقى فرقُ الدرج منسوبًا إلى من عدّه.
      </Banner>

      {/*
        السلّةُ مسوّدةٌ على الخادم، فلا تضيع بالخروج — لكنّها لا تُستأنَف
        تلقائيًّا بعد الدخول، والكاشيرُ التالي لا يراها. فيُقال قبل الخروج.
      */}
      {hasCart && (
        <Banner tone="warn">
          في السلّة أصنافٌ لم تُدفع. اخرجْ وهي باقيةٌ مسوّدةً، لكن لن تفتحها
          الشاشةُ من جديد — والأنظفُ أن تُتِمَّها أو تُلغيَها أوّلًا.
        </Banner>
      )}

        <TouchButton wide onClick={onStay}>
          ابقَ — رجوعٌ إلى البيع
        </TouchButton>

        <button
          className="min-h-14 rounded-lg border border-line text-base text-ink-soft"
          type="button"
          onClick={onLeave}
        >
          اخرج الآن
        </button>
      </div>
    </Sheet>
  )
}

/**
 * اختيارُ المستودع — <b>ويُسأل ولا يُفترض</b>.
 *
 * وشاشةٌ مستقلّةٌ لا سطرٌ في القشرة: الكاشيرُ يفتح جهازَه صباحًا فيجد سؤالًا
 * واحدًا بإجابةٍ واحدة، ثمّ لا يراه بقيّةَ اليوم.
 */
function WarehouseGate({
  warehouses,
  onChoose,
  onLogout,
}: {
  warehouses: Warehouse[]
  onChoose: (id: string) => void
  onLogout: () => void
}) {
  return (
    <div className="mx-auto grid min-h-dvh max-w-md content-center gap-3 p-6">
      <h1 className="text-center text-xl font-bold">من أيّ مستودعٍ تبيع؟</h1>

      <Banner tone="info">
        ولا يُفترض عنك: البيعُ من «التالف» بدل «الرئيسيّ» خطأٌ لا يُكتشف إلا بجرد.
      </Banner>

      {warehouses.map((warehouse) => (
        <TouchButton key={warehouse.id} wide onClick={() => onChoose(warehouse.id)}>
          {warehouse.nameAr}
        </TouchButton>
      ))}

      <button className="min-h-11 text-sm text-ink-soft underline" type="button" onClick={onLogout}>
        خروج
      </button>
    </div>
  )
}

function HeaderButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      className="min-h-11 rounded-lg border border-line px-3 text-sm"
      type="button"
      onClick={onClick}
    >
      {label}
    </button>
  )
}

function Blocked({ children, onLogout }: { children: React.ReactNode; onLogout: () => void }) {
  return (
    <div className="mx-auto grid min-h-dvh max-w-md content-center gap-4 p-6 text-center">
      <Banner tone="warn">{children}</Banner>

      <TouchButton wide onClick={onLogout}>
        خروج
      </TouchButton>

      <p className="text-xs text-ink-soft">{BRAND.nameAr}</p>
    </div>
  )
}
